import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';

import Button from '@adsk/bim360-matrix-react-button';
import SelectDropdown from '@adsk/bim360-matrix-react-select-dropdown';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';

import './_SplitButton.scss';

/**
 * SplitButton - A button combining SelectDropdown and Button.
 * The dropdown can appear either on the left or the right of the Action Button and can display SVGs along the labels.
 * Please check the Storybook for a complete demo of this component
 */
export default class SplitButton extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedOption: this.props.defaultOption,
      isOpen: false
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // If defaultOption changes it means that we want to force the current selected option to the new default option
    if (
      !isEqual(this.props.defaultOption, nextProps.defaultOption) ||
      this.props.defaultOption !== nextProps.defaultOption
    ) {
      this.setState({ selectedOption: nextProps.defaultOption });
    }
  }

  _handleDropdownOnChange(option) {
    this.setState({ selectedOption: option });
    this.props.onDropdownSelect(option);
  }

  optionRenderer(value) {
    const option = this.props.options.find(option => option.value === value);

    if (option.svgId || option.svg) {
      return (
        <div className="SplitButton__icon-option-container">
          <SvgIcon className="SplitButton__svg-option" svgId={option.svgId}>
            {option.svgId ? undefined : option.svg}
          </SvgIcon>
          <span className="SplitButton__option-label">{option.label}</span>
        </div>
      );
    }

    return option.label;
  }

  valueRenderer(value) {
    const option = this.props.options.find(option => option.value === value);

    if (option.svgId || option.svg) {
      return (
        <SvgIcon className="SplitButton__svg-value" svgId={option.svgId}>
          {option.svgId ? undefined : option.svg}
        </SvgIcon>
      );
    }

    return null;
  }

  _renderDropdownButton(additionalDropdownProps) {
    const dropdownLocation = this.props.isDropdownOnLeft ? 'left' : 'right';
    const classes = classNames(
      'SplitButton__dropdown-button',
      `SplitButton__dropdown-button--${dropdownLocation}`,
      this.props.dropdownButtonClassName,
      {
        'SplitButton__dropdown-button--empty-value':
          !this.state.selectedOption ||
          !(this.state.selectedOption.svgId || this.state.selectedOption.svg)
      },
      { 'SplitButton__dropdown-button--is-open': this.state.isOpen },
      { 'SplitButton__dropdown-button--is-disabled': this.props.isDisabled }
    );

    return (
      <div className={classes} style={this.props.dropdownButtonStyle}>
        <SelectDropdown
          {...additionalDropdownProps}
          options={this.props.options}
          selectedOptions={this.state.selectedOption}
          onChange={option => this._handleDropdownOnChange(option)}
          optionRenderer={(value, label) => this.optionRenderer(value)}
          valueRenderer={(value, label) => this.valueRenderer(value)}
          placeholder={''}
          onOpen={() => {
            this.setState({ isOpen: true });
          }}
          onClose={() => {
            this.setState({ isOpen: false });
          }}
          disabled={this.props.isDisabled}
        />
      </div>
    );
  }

  _renderActionButton(actionButtonProps, actionButtonClassName, actionButtonStyle) {
    const dropdownLocation = this.props.isDropdownOnLeft ? 'left' : 'right';
    const classes = classNames(
      'SplitButton__action-button',
      `SplitButton__action-button--dropdown-${dropdownLocation}`,
      actionButtonClassName
    );

    return (
      <Button
        {...actionButtonProps}
        className={classes}
        styleType={actionButtonProps.styleType || Button.StyleType.DEFAULT}
        style={actionButtonStyle}
        onClick={() => this.props.onActionButtonClick(this.state.selectedOption)}
        disabled={this.props.isDisabled}
      >
        {this.props.actionButtonLabel || this.state.selectedOption.label}
      </Button>
    );
  }

  render() {
    /* eslint-disable no-unused-vars */
    const {
      options,
      defaultOption,
      isDisabled,
      additionalDropdownProps,
      actionButtonProps,
      isDropdownOnLeft,
      onDropdownSelect,
      onActionButtonClick,
      actionButtonLabel,
      containerClassName,
      dropdownButtonClassName,
      actionButtonClassName,
      containerStyle,
      dropdownButtonStyle,
      actionButtonStyle,
      ...rest
    } = this.props;
    /* eslint-enable no-unused-vars */
    return (
      <div
        {...rest}
        className={classNames('SplitButton', containerClassName)}
        style={containerStyle}
      >
        {isDropdownOnLeft && this._renderDropdownButton(additionalDropdownProps)}
        {this._renderActionButton(actionButtonProps, actionButtonClassName, actionButtonStyle)}
        {!isDropdownOnLeft && this._renderDropdownButton(additionalDropdownProps)}
      </div>
    );
  }
}

SplitButton.defaultProps = {
  isDropdownOnLeft: true,
  defaultOption: {},
  onDropdownSelect: () => {},
  isDisabled: false,
  actionButtonProps: {}
};

const optionPropType = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  svgId: PropTypes.string,
  svg: PropTypes.oneOfType([PropTypes.func, PropTypes.node])
});

SplitButton.propTypes = {
  /**
   * Dropdown options with optional svgId/svg to render next to the options. label can either be a string or a Node element.
   */
  options: PropTypes.arrayOf(optionPropType).isRequired,
  /**
   * Default selected option. If changed after mount the the selected option will change to the new default option
   */
  defaultOption: optionPropType.isRequired,
  /**
   * Specifies if SplitButton is disabled
   */
  isDisabled: PropTypes.bool,
  /**
   * Option additional props to pass to SelectDropdown component
   */
  additionalDropdownProps: PropTypes.object,
  /**
   * Option additional props to pass to Button component
   */
  actionButtonProps: PropTypes.object,
  /**
   * Specifies if the dropdown button should be located on the left of the action button or on the right. Defaults to true (=left)
   */
  isDropdownOnLeft: PropTypes.bool,
  /**
   * An optional callback to run when an option is being selected from the dropdown menu. The selected option is being passed to this callback
   */
  onDropdownSelect: PropTypes.func,
  /**
   * A callback to run when the action button is clicked. The selected option is being passed to this callback
   */
  onActionButtonClick: PropTypes.func.isRequired,
  /**
   * The action button's label. If not passed then will fallback to the selected option's label
   */
  actionButtonLabel: PropTypes.string,
  /**
   * An optional class name to pass the SplitButton container
   */
  containerClassName: PropTypes.string,
  /**
   * An optional class name to pass the SelectDropdown container
   */
  dropdownButtonClassName: PropTypes.string,
  /**
   * An optional class name to pass the Action Button container
   */
  actionButtonClassName: PropTypes.string,
  /**
   * An optional styling to pass the SplitButton container
   */
  containerStyle: PropTypes.object,
  /**
   * An optional styling to pass the SelectDropdown container
   */
  dropdownButtonStyle: PropTypes.object,
  /**
   * An optional styling to pass the Action Button container
   */
  actionButtonStyle: PropTypes.object
};
